import React, { useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Mail, Lock, ShieldCheck, CalendarCheck, Car } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import JunagoLogo from "../components/JunagoLogo";

const CLIENT_IMG = "https://images.unsplash.com/photo-1772155836202-1e6d2ae10239?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBjYXIlMjByb2FkJTIwdHJpcCUyMHZhY2F0aW9uJTIwY29hc3RhbHxlbnwwfHx8fDE3NzY0Njc4NTh8MA&ixlib=rb-4.1.0&q=85";

const CTX = {
  client: {
    title: "Gérez vos réservations",
    desc: "Accédez à vos locations, suivez vos réservations et préparez vos prochains voyages en toute simplicité.",
    regText: "Pas encore de compte ?",
    regLink: "/register",
    regLabel: "Créez-en un",
    panel: "image",
  },
  vendor: {
    title: "Accédez à votre espace loueur",
    desc: "Gérez vos véhicules, vos réservations et vos revenus depuis un seul outil.",
    regText: "Pas encore loueur ?",
    regLink: "/inscription-loueur",
    regLabel: "Inscrivez-vous",
    panel: "gradient",
  },
};

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const from = location.state?.from?.pathname;
  const ctx = searchParams.get("context") || "client";
  const c = CTX[ctx] || CTX.client;

  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    const dest = from || (user.role === "vendor" ? "/vendor/pilotage" : "/mes-reservations");
    return <Navigate to={dest} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(form.email, form.password);
      const dest = from || (u.role === "vendor" ? "/vendor/pilotage" : "/mes-reservations");
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left panel */}
      {c.panel === "image" ? (
        <div className="hidden lg:block relative overflow-hidden">
          <img src={CLIENT_IMG} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 via-slate-900/50 to-slate-900/30" />
          <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white">
            <Link to="/"><JunagoLogo size={34} tone="light" /></Link>
            <div>
              <h1 className="font-heading text-4xl font-medium tracking-tight max-w-sm leading-tight" data-testid="login-panel-title">
                {c.title}
              </h1>
              <p className="text-slate-200 mt-3 max-w-sm leading-relaxed text-sm">{c.desc}</p>
              <div className="flex items-center gap-6 mt-8 text-sm text-slate-300">
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-violet-400" /> Paiement sécurisé</span>
                <span className="inline-flex items-center gap-1.5"><CalendarCheck className="h-4 w-4 text-violet-400" /> Confirmation instantanée</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="hidden lg:block relative overflow-hidden bg-slate-900">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-violet-900/80" />
          <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white">
            <Link to="/"><JunagoLogo size={34} tone="light" /></Link>
            <div>
              <div className="text-xs tracking-[0.22em] uppercase font-bold text-violet-300 mb-3">Espace professionnel</div>
              <h1 className="font-heading text-4xl font-medium tracking-tight max-w-sm leading-tight" data-testid="login-panel-title">
                Pilotez votre flotte depuis un seul outil.
              </h1>
              <p className="text-slate-200 mt-3 max-w-sm leading-relaxed text-sm">{c.desc}</p>
              <div className="flex flex-col gap-2 mt-8 text-sm text-slate-300">
                <span className="inline-flex items-center gap-2"><Car className="h-4 w-4 text-violet-400" /> Contrats & états des lieux automatisés</span>
                <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-violet-400" /> Encaissements sécurisés via Stripe</span>
                <span className="inline-flex items-center gap-2"><CalendarCheck className="h-4 w-4 text-violet-400" /> Calendrier de flotte & tâches automatiques</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Right panel — Form */}
      <div className="flex flex-col justify-center px-6 sm:px-12 py-12 bg-white">
        <div className="max-w-sm w-full mx-auto">
          <Link to="/" className="lg:hidden inline-block mb-10"><JunagoLogo size={34} tone="dark" /></Link>

          <h2 className="font-heading text-3xl font-medium tracking-tight text-slate-900" data-testid="login-title">{c.title}</h2>
          <p className="text-sm text-slate-500 mt-2 leading-relaxed lg:hidden" data-testid="login-desc">{c.desc}</p>
          <p className="text-sm text-slate-500 mt-3">
            {c.regText}{" "}
            <Link to={c.regLink} className="text-violet-600 font-medium hover:underline" data-testid="go-register-link">{c.regLabel}</Link>
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Email</label>
              <div className="mt-1.5 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-600 focus:border-violet-600" placeholder="vous@exemple.com" data-testid="login-email" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Mot de passe</label>
                <Link to="/mot-de-passe-oublie" className="text-xs font-medium text-violet-600 hover:underline" data-testid="forgot-password-link">
                  Mot de passe oublié ?
                </Link>
              </div>
              <div className="mt-1.5 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-600 focus:border-violet-600" placeholder="Votre mot de passe" data-testid="login-password" />
              </div>
            </div>

            {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3" data-testid="login-error">{error}</div>}

            <button type="submit" disabled={loading} className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-full btn-primary font-semibold text-sm disabled:opacity-50" data-testid="login-submit">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Se connecter
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
